/**
 * Exports an array of records to a CSV file and triggers a browser download.
 *
 * @param {Object[]} data       - Array of records to export
 * @param {string}   filename   - Base filename without extension (date is appended)
 * @param {Array<{
 *   key: string,
 *   label: string,
 *   format?: (value: any, row: Object) => string
 * }>} columns - Column definitions
 * @returns {boolean} true if exported, false if data was empty
 */
export const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) return false;

  // Build header row
  const header = columns
    .map((col) => `"${String(col.label).replace(/"/g, '""')}"`)
    .join(',');

  // Build data rows
  const rows = data.map((row) =>
    columns
      .map((col) => {
        let value = row[col.key];

        if (col.format) {
          value = col.format(value, row);
        } else if (value === null || value === undefined) {
          value = '';
        } else if (typeof value === 'boolean') {
          value = value ? 'Yes' : 'No';
        } else if (typeof value === 'number') {
          value = value.toString();
        }

        // Escape double-quotes and wrap every cell in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(',')
  );

  const csv = [header, ...rows].join('\n');

  // UTF-8 BOM so Excel opens Thai / special characters correctly
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

  const today = new Date().toISOString().split('T')[0];
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return true;
};
